import React, { useCallback, useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { useConst, useBoolean } from '@fluentui/react-hooks';
import { IPublicClientApplication } from '@azure/msal-browser';
import { mergeStyleSets, FontSizes, FontWeights, Persona, PersonaSize, DefaultButton, IContextualMenuProps, PersonaInitialsColor, Icon, NeutralColors, ContextualMenuItemType } from '@fluentui/react';

import { idleTimeoutMs } from 'auth/authConfig';
import { upTosmallScreenSelector, orientationLandscape } from 'utils/breakpoints';
import TranslationsContext from 'translations/TranslationsContext';
import translationKeys from 'translationKeys';
import { LanguageSelectorModal } from 'translations/LanguageSelectorModal';

const Gear = () => <Icon iconName="Settings" style={{ fontSize: '1rem' }}/>;

interface LayoutProps {
	children: JSX.Element
}

export const Layout = ({ children }: LayoutProps) => {
	const navigate  = useNavigate();
	const { t } = useContext(TranslationsContext)!;

	const { instance } = useMsal();
	const [account] = instance.getAllAccounts();
	const [ isLanguageSelectorOpen, { toggle: toggleLanguageSelectorModal, setFalse: hideLanguageSelector } ] = useBoolean(false);

	const handleLogout = useCallback((instance: IPublicClientApplication) => {
		instance.logoutRedirect().catch(e => {
			console.error(e);
		});
	}, []);

	useIdleTimer({ onIdle: () => handleLogout(instance), crossTab: true, timeout: idleTimeoutMs, name: 'mc4n-installer' });

	const menuProps = useConst<IContextualMenuProps>(() => ({
		items: [
			{ key: 'account', ariaLabel: t(translationKeys.Layout.MenuProps.Item.One.AriaLabel), role: 'link', text: t(translationKeys.Layout.MenuProps.Item.One.Text), onClick: () => { window.open('https://myaccount.microsoft.com', '_blank'); } },
			{ key: 'signout', ariaLabel: t(translationKeys.Layout.MenuProps.Item.Two.AriaLabel), text: t(translationKeys.Layout.MenuProps.Item.Two.Text), onClick: () => handleLogout(instance) }
		]
	}));

	const navigationMenuProps = useConst<IContextualMenuProps>(() => ({
		items: [
			{ key: 'installer', ariaLabel: t(translationKeys.Layout.NavigationMenuProps.Item.One.AriaLabel), text: t(translationKeys.Layout.NavigationMenuProps.Item.One.Text), onClick: () => navigate('/') },
			{ key: 'environmentCheck', ariaLabel: t(translationKeys.Layout.NavigationMenuProps.Item.Two.AriaLabel), text: t(translationKeys.Layout.NavigationMenuProps.Item.Two.Text), onClick: () => navigate('/environmentcheck') },
			{ key: 'documentation', ariaLabel: t(translationKeys.Layout.NavigationMenuProps.Item.Three.AriaLabel), role: 'link', text: t(translationKeys.Layout.NavigationMenuProps.Item.Three.Text), onClick: () => { window.open('https://aka.ms/mc4n/installer/docs', '_blank'); } },
			{ key: 'divider', itemType: ContextualMenuItemType.Divider },
			{ key: 'changeLanguage', ariaLabel: t(translationKeys.Layout.NavigationMenuProps.Item.Four.AriaLabel), role: 'button', text: t(translationKeys.Layout.NavigationMenuProps.Item.Four.Text), onClick: () => toggleLanguageSelectorModal() }
		]
	}));

	return (
		<div>
			<header aria-labelledby="app-title" className={style.header}>
				<div id="app-title" className={style.headerTitle}>{t(translationKeys.Layout.Component.Title)}</div>
				<nav className={style.nav} aria-label={t(translationKeys.Layout.Component.Nav.AriaLabel)}>
					<DefaultButton
						aria-label={t(translationKeys.Layout.Component.SettingsButton.AriaLabel)}
						className={style.gearButton}
						onRenderMenuIcon={() => null}
						menuProps={navigationMenuProps}
						checked={true}
						onRenderIcon={() =>
							<Gear />
						}
					/>
					<DefaultButton
						aria-label={t(translationKeys.Layout.Component.ProfileButton.AriaLabel)}
						className={style.profileButton}
						onRenderMenuIcon={() => null}
						menuProps={menuProps}
						onRenderIcon={() =>
							<Persona
								initialsColor={PersonaInitialsColor.lightBlue}
								hidePersonaDetails
								text={account?.name}
								size={PersonaSize.size32}
							/>
						}
					/>
				</nav>
			</header>
			<div className={style.content}>
				{children}
			</div>
			<LanguageSelectorModal isOpen={isLanguageSelectorOpen} onDismiss={hideLanguageSelector}/>
		</div>
	);
};

const HEADER_HEIGHT = '48px';

const style = mergeStyleSets({
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		padding: 'auto 0 auto 0.75rem',
		height: HEADER_HEIGHT,
		background: 'black',
		color: 'white'
	},
	nav: {
		padding: 0,
		margin: 0,
		display: 'flex',
		alignItems: 'center'
	},
	profileButton: {
		minWidth: 0,
		height: HEADER_HEIGHT,
		width: HEADER_HEIGHT,
		padding: 0,
		margin: 0,
		background: 'transparent',
		border: 'none',
		borderRadius: 0,
		'&:hover, &:active, &.is-expanded': {
			background: NeutralColors.gray20
		}
	},
	gearButton: {
		height: HEADER_HEIGHT,
		width: HEADER_HEIGHT,
		minWidth: 0,
		padding: 0,
		margin: 0,
		background: 'transparent',
		border: 'none',
		borderRadius: 0,
		color: 'white',
		'&:hover, &:active, &.is-expanded': {
			background: NeutralColors.gray20,
			color: NeutralColors.gray190
		}
	},
	headerTitle: {
		fontSize: FontSizes.size16,
		fontWeight: FontWeights.semibold
	},
	content: {
		height: `calc(100vh - ${HEADER_HEIGHT})`,
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		selectors: {
			[orientationLandscape(upTosmallScreenSelector)]: {
				height: 'initial'
			}
		}
	}
});
